import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { API, type ApiError } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import type { Categoria, Movimiento } from '../api/types';
import { cop, fecha, hoy, num, toDateValue } from '../lib/format';
import { downloadXlsx, FMT_COP, FMT_DATE, FMT_NUM, fileNameConFecha } from '../lib/xlsx';
import { PageHeader } from '../components/PageHeader';
import { FilterBar } from '../components/FilterBar';
import { ResponsiveTable, type Column } from '../components/ResponsiveTable';

type ListaMov = { items: Movimiento[]; total: number };

type FormVenta = {
  fecha: string;
  paquetes: string;
  precioUnitario: string;
  monto: string;
  metodoPago: string;
  descripcion: string;
};

const empty: FormVenta = {
  fecha: hoy(),
  paquetes: '',
  precioUnitario: '',
  monto: '',
  metodoPago: 'Efectivo',
  descripcion: '',
};

function moneyToNumber(v: string): number {
  const n = Number(v.replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
}

export function VentasPage() {
  const { tiene } = useAuth();
  const qc = useQueryClient();
  const [form, setForm] = useState<FormVenta>(empty);
  const [desde, setDesde] = useState(hoy());
  const [hasta, setHasta] = useState(hoy());
  const [error, setError] = useState<string | null>(null);

  const catsQ = useQuery({
    queryKey: ['categorias'],
    queryFn: () => API.get<Categoria[]>('/categorias'),
    enabled: tiene('categorias:read'),
  });

  const categoriaVentas = useMemo(
    () => (catsQ.data ?? []).find((c) => !c.padreId && c.nombre.toLowerCase() === 'ventas') ?? null,
    [catsQ.data],
  );

  const subcategoriaArepas = useMemo(
    () => (catsQ.data ?? []).find((c) => c.padreId === categoriaVentas?.id && c.nombre.toLowerCase() === 'arepas') ?? null,
    [catsQ.data, categoriaVentas],
  );

  const ventasQ = useQuery({
    queryKey: ['ventas-reales', categoriaVentas?.id, desde, hasta],
    queryFn: () => {
      const p = new URLSearchParams();
      p.set('tipo', 'ingreso');
      if (categoriaVentas?.id) p.set('categoriaId', categoriaVentas.id);
      if (desde) p.set('desde', desde);
      if (hasta) p.set('hasta', hasta);
      p.set('limit', '300');
      return API.get<ListaMov>(`/movimientos?${p.toString()}`);
    },
    enabled: tiene('movimientos:read') && !!categoriaVentas?.id,
  });

  const totalVendido = (ventasQ.data?.items ?? []).reduce((acc, v) => acc + Number(v.monto), 0);
  const totalPaquetes = (ventasQ.data?.items ?? []).reduce((acc, v) => acc + Number(v.cantidad ?? 0), 0);
  const precioPromedio = totalPaquetes > 0 ? totalVendido / totalPaquetes : 0;
  const ventaPreview = moneyToNumber(form.paquetes) * moneyToNumber(form.precioUnitario);

  const registrar = useMutation({
    mutationFn: (body: Record<string, unknown>) => API.post<Movimiento>('/movimientos', body),
    onSuccess: () => {
      setForm(empty);
      setError(null);
      qc.invalidateQueries({ queryKey: ['ventas-reales'] });
      qc.invalidateQueries({ queryKey: ['movimientos'] });
      qc.invalidateQueries({ queryKey: ['saldo'] });
      qc.invalidateQueries({ queryKey: ['reporte-mensual'] });
      qc.invalidateQueries({ queryKey: ['reporte-proyeccion'] });
    },
    onError: (err) => setError((err as unknown as ApiError).message ?? 'No se pudo registrar la venta'),
  });

  function submit(e: React.FormEvent) {
    e.preventDefault();
    const paquetes = moneyToNumber(form.paquetes);
    const precioUnitario = moneyToNumber(form.precioUnitario);
    const monto = form.monto ? moneyToNumber(form.monto) : paquetes * precioUnitario;

    if (!categoriaVentas) {
      setError('No existe la categoria Ventas. Creala en Configuracion > Categorias antes de registrar ventas.');
      return;
    }
    if (paquetes <= 0) {
      setError('Ingresa cuantos paquetes vendiste.');
      return;
    }
    if (monto <= 0) {
      setError('El valor recibido debe ser mayor que cero.');
      return;
    }

    registrar.mutate({
      fecha: form.fecha,
      tipo: 'ingreso',
      categoriaId: categoriaVentas.id,
      subcategoriaId: subcategoriaArepas?.id ?? null,
      cantidad: paquetes,
      unidad: 'paq',
      descripcion: form.descripcion || `Venta real (${num(paquetes, 0)} paq.)`,
      metodoPago: form.metodoPago || null,
      monto,
    });
  }

  function exportarXlsx() {
    const items = ventasQ.data?.items ?? [];
    if (items.length === 0) return;
    downloadXlsx(fileNameConFecha('ventas-reales'), [
      {
        name: 'Ventas reales',
        rows: items,
        columns: [
          { header: 'Fecha', value: (m) => toDateValue(m.fecha), width: 12, numFmt: FMT_DATE },
          { header: 'Paquetes', value: (m) => Number(m.cantidad ?? 0), width: 10, numFmt: FMT_NUM },
          {
            header: 'Precio promedio',
            value: (m) => {
              const paquetes = Number(m.cantidad ?? 0);
              return paquetes > 0 ? Number(m.monto) / paquetes : null;
            },
            width: 14,
            numFmt: FMT_COP,
          },
          { header: 'Total recibido', value: (m) => Number(m.monto), width: 16, numFmt: FMT_COP },
          { header: 'Metodo de pago', value: (m) => m.metodoPago ?? '', width: 14 },
          { header: 'Descripcion', value: (m) => m.descripcion ?? '', width: 30 },
        ],
      },
    ]);
  }

  const columns: Column<Movimiento>[] = [
    {
      key: 'venta',
      header: 'Venta',
      mobile: 'primary',
      render: (m) => (
        <div>
          <div className="font-semibold text-slate-900">{m.descripcion ?? 'Venta real'}</div>
          <div className="text-xs text-slate-500">{fecha(m.fecha)} · {m.metodoPago ?? 'Sin metodo'}</div>
        </div>
      ),
    },
    {
      key: 'monto',
      header: 'Recibido',
      mobile: 'secondary',
      align: 'right',
      render: (m) => <span className="font-semibold text-emerald-700">{cop(Number(m.monto))}</span>,
    },
    {
      key: 'paquetes',
      header: 'Paquetes',
      align: 'right',
      render: (m) => (m.cantidad ? `${num(Number(m.cantidad), 0)} paq.` : '-'),
    },
    {
      key: 'unit',
      header: 'Promedio/paq.',
      align: 'right',
      render: (m) => {
        const paquetes = Number(m.cantidad ?? 0);
        return paquetes > 0 ? cop(Number(m.monto) / paquetes) : '-';
      },
    },
    { key: 'pago', header: 'Metodo', render: (m) => m.metodoPago ?? '-' },
  ];

  return (
    <div className="space-y-4 sm:space-y-6">
      <PageHeader
        title="Ventas reales"
        subtitle="Caja registradora: registra solo el dinero que realmente entra por ventas."
      />

      <section className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3">
        <div className="card px-3 py-3 sm:px-4">
          <div className="text-[10px] sm:text-xs uppercase tracking-wide text-slate-500">Recibido</div>
          <div className="mt-1 text-lg sm:text-xl font-semibold text-emerald-700">{cop(totalVendido)}</div>
        </div>
        <div className="card px-3 py-3 sm:px-4">
          <div className="text-[10px] sm:text-xs uppercase tracking-wide text-slate-500">Paquetes vendidos</div>
          <div className="mt-1 text-lg sm:text-xl font-semibold text-slate-950">{num(totalPaquetes, 0)}</div>
        </div>
        <div className="card px-3 py-3 sm:px-4">
          <div className="text-[10px] sm:text-xs uppercase tracking-wide text-slate-500">Promedio/paq.</div>
          <div className="mt-1 text-lg sm:text-xl font-semibold text-slate-950">{cop(precioPromedio)}</div>
        </div>
        <div className="card px-3 py-3 sm:px-4">
          <div className="text-[10px] sm:text-xs uppercase tracking-wide text-slate-500">Registros</div>
          <div className="mt-1 text-lg sm:text-xl font-semibold text-slate-950">{ventasQ.data?.total ?? 0}</div>
        </div>
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6">
        {tiene('movimientos:write') && (
          <div className="card p-4 sm:p-5 space-y-4 lg:sticky lg:top-6 lg:self-start">
            <div>
              <h2 className="font-semibold text-slate-950">Registrar entrada</h2>
              <p className="mt-1 text-xs text-slate-500">Usa esto cuando recibas efectivo, transferencia o pago digital.</p>
            </div>

            <form className="space-y-3" onSubmit={submit}>
              <div className="grid grid-cols-1 xs:grid-cols-2 gap-3">
                <div>
                  <label className="label">Fecha</label>
                  <input type="date" className="input" value={form.fecha} onChange={(e) => setForm((s) => ({ ...s, fecha: e.target.value }))} required />
                </div>
                <div>
                  <label className="label">Metodo de pago</label>
                  <select className="input" value={form.metodoPago} onChange={(e) => setForm((s) => ({ ...s, metodoPago: e.target.value }))}>
                    <option value="Efectivo">Efectivo</option>
                    <option value="Nequi">Nequi</option>
                    <option value="Daviplata">Daviplata</option>
                    <option value="Bancolombia">Bancolombia</option>
                    <option value="Transferencia">Transferencia</option>
                    <option value="Tarjeta">Tarjeta</option>
                    <option value="Otro">Otro</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 xs:grid-cols-2 gap-3">
                <div>
                  <label className="label">Paquetes vendidos</label>
                  <input type="number" min="0" step="1" inputMode="numeric" className="input" value={form.paquetes} onChange={(e) => setForm((s) => ({ ...s, paquetes: e.target.value }))} required />
                </div>
                <div>
                  <label className="label">Precio por paquete</label>
                  <input type="number" min="0" step="any" inputMode="decimal" className="input" value={form.precioUnitario} onChange={(e) => setForm((s) => ({ ...s, precioUnitario: e.target.value }))} />
                </div>
              </div>

              <div>
                <label className="label">Total recibido</label>
                <input
                  type="number"
                  min="0"
                  step="any"
                  inputMode="decimal"
                  className="input"
                  value={form.monto || (ventaPreview > 0 ? String(ventaPreview) : '')}
                  onChange={(e) => setForm((s) => ({ ...s, monto: e.target.value }))}
                  required
                />
                {ventaPreview > 0 && !form.monto && (
                  <div className="mt-1 text-[11px] text-slate-500">Calculado: {cop(ventaPreview)}</div>
                )}
              </div>

              <div>
                <label className="label">Nota</label>
                <input className="input" value={form.descripcion} onChange={(e) => setForm((s) => ({ ...s, descripcion: e.target.value }))} placeholder="Ej: venta local, domicilio, pedido cliente" />
              </div>

              {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2">{error}</div>}

              <button type="submit" className="btn-primary w-full" disabled={registrar.isPending || catsQ.isLoading}>
                {registrar.isPending ? 'Registrando...' : 'Registrar venta'}
              </button>
            </form>
          </div>
        )}

        <div className={`${tiene('movimientos:write') ? 'lg:col-span-2' : 'lg:col-span-3'} card`}>
          <div className="p-3 sm:p-4 border-b border-slate-200 space-y-3 bg-slate-50/60 rounded-t-lg">
            <FilterBar activeCount={(desde ? 1 : 0) + (hasta ? 1 : 0)} title="Rango">
              <div>
                <label className="label">Desde</label>
                <input type="date" className="input" value={desde} onChange={(e) => setDesde(e.target.value)} />
              </div>
              <div>
                <label className="label">Hasta</label>
                <input type="date" className="input" value={hasta} onChange={(e) => setHasta(e.target.value)} />
              </div>
            </FilterBar>

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-sm text-slate-600">
              <div className="flex flex-wrap gap-x-4 gap-y-1">
                <span><b>{ventasQ.data?.total ?? 0}</b> ventas</span>
                <span className="font-semibold text-emerald-700">{cop(totalVendido)} recibido</span>
              </div>
              <button type="button" className="btn-secondary w-full sm:w-auto" onClick={exportarXlsx} disabled={!ventasQ.data?.items?.length}>
                Exportar .xlsx
              </button>
            </div>
          </div>

          <ResponsiveTable<Movimiento>
            columns={columns}
            rows={ventasQ.data?.items}
            rowKey={(m) => m.id}
            loading={ventasQ.isLoading || catsQ.isLoading}
            empty={categoriaVentas ? 'Sin ventas reales en este rango.' : 'No existe la categoria Ventas.'}
          />
        </div>
      </div>
    </div>
  );
}
